import React, { useState, useEffect } from 'react'
import {
    Box,
    Button,
    Divider,
    Flex,
    Step,
    StepIcon,
    StepIndicator,
    StepNumber,
    Stepper,
    StepSeparator,
    StepStatus,
    Text,
    useSteps,
    VStack,
    Spinner,
    Alert,
    AlertIcon,
    AlertDescription,
    Center
} from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../../../components/Header'
import {  } from '@chakra-ui/icons';
import { CampaignDetails } from './CampaignDetails/CampaignDetails'
import { CommunicationChannels } from './CommunicationChannels/CommunicationChannels'
import { TemplateSelection } from './TemplateSelection/TemplateSelection'
import { TargetContacts } from './TargetContacts/TargetContact'
import TemplateVariableConfig from './TemplateVariableConfig/TemplateVariableConfig'
import CampaignReviewSubmit from './ReviewCampaign/ReviewCampaign'
import { fetchWrapper } from '../../../helpers'
import { LuArrowLeft } from 'react-icons/lu'
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper'
// moment-timezone, so moment.tz here does not depend on some other module
// having imported it first
import moment from 'moment-timezone'
import API from '../../../helpers/axiosInstance'
import { getApiHeaders, hasPermission } from '../../../utils/DefaultFunction'

const EditCampaign = ({ header }) => {
    const navigate = useNavigate()
    const { id } = useParams() // Get campaignId from URL
    const [currentStep, setCurrentStep] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)

    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const permParams = "marketing/campaign";

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const xPermission = hasPermission("campaign-edit", permissions);

    const [formData, setFormData] = useState({
        // Campaign Details section
        campaignName: '',
        campaignType: '',
        description: '',

        // Communication Channels section
        selectedChannels: [],

        // Template Selection section
        selectedTemplate: null,
        sendToAll: false,
        selectedTags: [],
        targetAudience: [],
        audienceCount: 0,
        template_variables: {}
    });
    const [cohorts, setCohorts] = useState([]);
    // Load existing campaign data for editing
    const loadCampaignData = async (id) => {
        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${id}`
            const response = await fetchWrapper.get(url)

            if (response.success) {
                const campaignData = response.data
                await populateFormData(campaignData)
            } else {
                throw new Error(response.message || 'Failed to load campaign')
            }
        } catch (err) {
            console.error('Failed to load campaign:', err)
            setError(err.message || 'Failed to load campaign data')
        } finally {
            setLoading(false)
        }
    }


    useEffect(() => {
        const loadCohorts = async () => {

            try {
                const url = `${process.env.REACT_APP_COHORT}/cohorts`;
                const data = await fetchWrapper.get(url);

                // Handle different response structures safely
                const cohortsList = data.data?.cohorts || data.cohorts || data || [];
                setCohorts(cohortsList);
            } catch (err) {
                console.error("Failed to fetch cohorts", err);
                setCohorts([]);
            } finally {

            }
        };


        loadCohorts();
    }, []);

    const populateFormData = async (campaignData) => {
        try {

            const contactsUrl = `${process.env.REACT_APP_API_URL}/contacts`;
            const contactsResponse = await fetchWrapper.get(contactsUrl);

            const contacts = contactsResponse?.data || [];

            // Tag mapping
            const tagMap = new Map();
            contacts.forEach(contact => {
                (contact.tags || []).forEach(tag => {
                    tagMap.set(tag._id, tag);
                });
            });

            const selectedTagObjects = (campaignData.target_tags || [])

            // ✅ Determine audience type
            let audience_type = "all_contacts"; // default
            if (campaignData.audience_type) {
                audience_type = campaignData.audience_type;
            } else if (
                !campaignData.sendToAll &&
                selectedTagObjects.length > 0
            ) {
                audience_type = "tagged_contacts";
            } else if (
                !campaignData.sendToAll &&
                selectedTagObjects.length === 0 &&
                campaignData.selectedCohort &&
                Object.keys(campaignData.selectedCohort).length > 0
            ) {
                audience_type = "cohort_contacts";
            }

            const sendToAll = audience_type === "all_contacts" || audience_type === "all_subscribers";
            const isTagged = audience_type === "tagged_contacts";
            const isCohort = audience_type === "cohort_contacts";

            // ✅ Handle cohort detection (even if no ID field)
            let selectedCohort = {};
            if (isCohort) {
                // Try by ID first
                if (campaignData.target_cohort_id) {
                    selectedCohort = cohorts?.find(
                        (c) => c._id === campaignData.target_cohort_id
                    ) || {};
                }
                // If no ID, try name match
                if (!selectedCohort._id && campaignData.selectedCohort?.name) {
                    selectedCohort = cohorts?.find(
                        (c) => c.name === campaignData.selectedCohort.name
                    ) || {};
                }
                // Or fallback to the first cohort (optional)
                if (!selectedCohort._id && cohorts.length === 1) {
                    selectedCohort = cohorts[0];
                }
            }

            // ✅ Determine target audience
            let targetAudience = [];
            if (sendToAll) {
                targetAudience = contacts;
            } else if (isTagged && selectedTagObjects.length > 0) {
                const selectedTagIds = selectedTagObjects.map(tag => tag._id);
                targetAudience = contacts.filter(contact =>
                    contact.tags?.some(tag => selectedTagIds.includes(tag._id))
                );
            } else if (isCohort && selectedCohort._id) {
                targetAudience = selectedCohort.contacts || [];
            }

            const campaignTypeMapping = {
                transactional: "1",
                promotional: "2",
                marketing: "3",
                informational: "4",
            };

            // ✅ Schedule handling
            let scheduleDate = "";
            let scheduleTime = "";
            if (
                campaignData.launch_type === "scheduled" &&
                campaignData.scheduled_at
            ) {
                // timezone_based campaigns can come back with 'all' (or a zone moment
                // cannot resolve) — fall back to the viewer's zone so the date/time
                // fields still populate instead of rendering "Invalid date".
                const savedTz = campaignData?.timezone;
                const tz = savedTz && savedTz !== 'all' && moment.tz.zone(savedTz)
                    ? savedTz
                    : moment.tz.guess();
                const d = moment
                    .utc(campaignData.scheduled_at)
                    .tz(tz)

                scheduleDate = d.format("YYYY-MM-DD")
                scheduleTime = d.format("HH:mm")

            }

            // Map API audience_type to UI audienceType (tags/cohort live under all_contacts)
            const uiAudienceType = audience_type === "all_subscribers" ? "all_subscribers" : "all_contacts";

            // ✅ Set final data
            setFormData({
                campaignName: campaignData.name || "",
                allContactsTotal: contactsResponse?.pagination,
                campaignType:
                    campaignTypeMapping[campaignData.campaign_type] ||
                    campaignData.campaignType ||
                    "",
                description: campaignData.description || "",
                selectedChannels: campaignData.selected_channels || campaignData.selectedChannels || [],
                selectedTemplate: campaignData.template || campaignData.selectedTemplate || null,
                sendToAll,
                audienceType: uiAudienceType,
                selectedTags: selectedTagObjects,
                selectedCohort, // ✅ detected cohort
                targetAudience,
                audienceCount: campaignData.total_audience || targetAudience.length,
                template_variables: campaignData.template_variables || {},
                conversion_event: campaignData.conversion_event || null,
                sender_id: campaignData.sender_id || null,
                email_sender_id: campaignData.email_sender_id || null,
                launchSchedule: {
                    type: campaignData.launch_type || campaignData.launchSchedule?.type,
                    ...(campaignData.launch_type === "scheduled" && {
                        date: scheduleDate,
                        time: scheduleTime,
                        timezone: campaignData?.timezone,
                        // per-timezone schedule, so editing keeps it instead of
                        // collapsing back to a single instant
                        timezoneBased: campaignData.timezone_based === 'Y',
                        campaignTime: campaignData.campaign_time || '',
                        contactTimezone: campaignData.contact_timezone || [],
                    }),
                },
            });

            setIsEditMode(true);
        } catch (err) {
            console.error("Error populating form data:", err);
            setError("Failed to load campaign details");
        }
    };



    // Check if we're in edit mode and load data — wait for the permission
    // check to finish and pass before firing the real data fetch.
    useEffect(() => {
        if (permissionsLoading || !xPermission) return;
        if (id && id !== 'add') {
            loadCampaignData(id)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, cohorts, permissionsLoading, xPermission])

    const handleContinue = () => {

        if (activeStep === 0) {
            // Step 0 validation
            if (!formData.campaignName) {
                alert('Please fill in campaign name')
                return
            }
            if (!formData.selectedTemplate?.id) {
                alert('Please select a template')
                return
            }
        }

        if (activeStep === 1) {
            // Step 1 validation
            if (!formData.sendToAll && formData.audienceCount === 0) {
                alert('Please select target audience')
                return
            }
        }

        if (activeStep === 2) {
            // Step 2 validation - template variables
            // Add any validation needed
        }

        if (activeStep === 3) {
            // Step 3 validation
            if (formData.selectedChannels.length === 0) {
                alert('Please select at least one communication channel')
                return
            }
        }

        // Don't auto-advance from the final step
        if (activeStep < 4) {
            setActiveStep(currentStep + 1)
            setCurrentStep(currentStep + 1)
        }
    }

    function handleBack() {
        if (currentStep > 0) {
            setActiveStep(currentStep - 1)
            setCurrentStep(currentStep - 1)
        }
    }

    const steps = [
        { title: 'Details', description: 'Campaign Info' },
        { title: 'Audience', description: 'Target Contacts' },
        { title: 'Variables', description: 'Template Config' },
        { title: 'Channels', description: 'Communication' },
        // { title: 'Review', description: 'Launch Campaign' }
    ]

    const { activeStep, setActiveStep } = useSteps({
        index: currentStep,
        count: steps.length,
    })

    const handleStepClick = (step) => {
        setActiveStep(step)
        setCurrentStep(step)
    }

    const handleNavigateBack = () => {
        navigate(-1)
    }


    if (permissionsLoading) {
        return (
            <>
                <Header header={header} />
                <Center h="400px"><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
            </>
        );
    }
    if (!xPermission) {
        return (
            <>
                <Header header={header} />
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                        <Text fontSize="sm" color="gray.500">You don't have permission to view this sections.</Text>
                    </Box>
                </Center>
            </>
        );
    }

    // Show loading state
    if (loading) {
        return (
            <>
                <Header header={header} />
                <Box style={{ width: "100%", padding: "60px" }} py={8}>
                    <Box textAlign="center" py={10}>
                        <Spinner size="xl" color="blue.500" />
                        <Text mt={4} color="gray.500">
                            {id ? 'Loading campaign data...' : 'Initializing...'}
                        </Text>
                    </Box>
                </Box>
            </>
        )
    }

    // Show error state
    if (error) {
        return (
            <>
                <Header />
                <Box style={{ width: "100%", padding: "60px" }} py={8}>
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                    <Button mt={4} onClick={handleNavigateBack}>
                        <LuArrowLeft />
                    </Button>
                </Box>
            </>
        )
    }

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
                <Box>

                    <Button

                        variant="outline"
                        size="sm"
                        borderRadius="md"
                        _hover={{ bg: "#0000001a !important", color: "black !important" }}
                        onClick={handleNavigateBack}
                    >
                        <LuArrowLeft style={{ marginRight: ".5rem" }} /> Back To Campaign
                    </Button>
                    <Flex direction="column">
                        <Text fontSize="2xl" fontWeight="semibold">
                            {isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold">
                            Step {activeStep + 1} of {steps.length}: {steps[activeStep]?.description}
                        </Text>
                        {/* {isEditMode && (
                            <Text fontSize="xs" color="blue.600" mt={1}>
                                Editing: {formData.campaignName}
                            </Text>
                        )} */}
                    </Flex>

                </Box>
                <Flex justifyContent={"center"} alignItems={"center"}>
                    <Box mt={8} width={"30%"}>


                        <Stepper
                            index={currentStep}
                            colorScheme='blue'

                            sx={{
                                '& .chakra-step__indicator[data-status="active"]': {
                                    backgroundColor: 'var(--brand-primary)',
                                    borderColor: 'var(--brand-primary)',
                                    color: 'white'
                                },
                                '& .chakra-step__indicator[data-status="complete"]': {
                                    backgroundColor: 'var(--brand-primary)',
                                    borderColor: 'var(--brand-primary)',
                                    color: 'white'
                                },
                                '& .chakra-step__separator[data-status="complete"]': {
                                    backgroundColor: 'var(--brand-primary)'
                                },
                                '& .chakra-step__indicator[data-status="incomplete"]': {
                                    borderColor: '#e2e8f0',
                                    backgroundColor: 'white',
                                    color: '#a0aec0'
                                }
                            }}
                        >
                            {steps.map((step, index) => (
                                <Step key={index} onClick={() => handleStepClick(index)}>
                                    <StepIndicator>
                                        <StepStatus
                                            complete={<StepIcon />}
                                            incomplete={<StepNumber />}
                                            active={<StepNumber />}
                                        />
                                    </StepIndicator>
                                    <StepSeparator />
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </Flex>
                <VStack spacing={6} align="stretch" mt={4}>
                    {activeStep === 0 && (
                        <>
                            <CampaignDetails formData={formData} setFormData={setFormData} />
                            <TemplateSelection formData={formData} setFormData={setFormData} />
                        </>
                    )}
                    {activeStep === 1 && (
                        <TargetContacts formData={formData} setFormData={setFormData} editable={true} />
                    )}
                    {activeStep === 2 && (
                        <TemplateVariableConfig formData={formData} setFormData={setFormData} />
                    )}
                    {activeStep === 3 && (
                        <>
                            <CommunicationChannels formData={formData} setFormData={setFormData} />
                            <Divider />
                            <CampaignReviewSubmit
                                formData={formData}
                                setFormData={setFormData}
                                isEditMode={isEditMode}
                                campaignId={id}
                                onSubmitSuccess={(response) => {

                                    navigate('/marketing/campaign')
                                }}
                            />
                        </>
                    )}

                    {activeStep < 3 && (
                        <Flex justify="space-between" pt={4} borderTop="1px solid" borderColor="gray.200">
                            <Button
                                variant="outline"
                                colorScheme="gray"
                                onClick={handleBack}
                                px={8}
                                disabled={activeStep === 0}
                            >
                                Back
                            </Button>

                            <Button
                                bg="var(--brand-primary)"
                                color="white"
                                onClick={handleContinue}
                                px={8}
                                _hover={{ bg: "var(--brand-primary-active)" }}
                            >
                                Continue
                            </Button>
                        </Flex>
                    )}
                </VStack>
            </BoxWrapper>
        </>
    )
}

export default EditCampaign